import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  AlertTriangle,
  Archive,
  Building2,
  Check,
  Copy,
  KeyRound,
  Laptop,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
} from "lucide-react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  DataTable,
  DetailList,
  DetailSection,
  DetailSheet,
  EmptyState,
  IconTile,
  Pagination,
  Panel,
  SearchInput,
  Segmented,
  SkeletonRows,
  StatCard,
  StatGrid,
  Tag,
  Toolbar,
  fmtDateBR,
  relativeDays,
  rowClass,
  td,
  th,
  usePagination,
  type Tone,
} from "@/components/superadmin/kit"
import {
  License,
  LicenseCompany,
  useDeleteLicense,
  useLicenses,
  useRenewLicense,
  useResetLicenseMachines,
  useSaveLicenseCompany,
  useToggleLicense,
} from "@/hooks/useLicenses"
import { cn } from "@/lib/utils"

import { LicenseModal } from "./LicenseModal"

const STATUS: Record<string, { label: string; tone: Tone }> = {
  trial: { label: "Avaliação", tone: "accent" },
  active: { label: "Ativa", tone: "success" },
  past_due: { label: "Inadimplente", tone: "warning" },
  suspended: { label: "Suspensa", tone: "warning" },
  revoked: { label: "Revogada", tone: "danger" },
  expired: { label: "Expirada", tone: "danger" },
  archived: { label: "Arquivada", tone: "neutral" },
}

const ATTENTION = ["past_due", "suspended", "expired", "revoked"]

function CopyKey({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-8 gap-1.5"
      onClick={async () => {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        toast.success("Chave copiada.")
        setTimeout(() => setCopied(false), 1800)
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copiada" : "Copiar chave"}
    </Button>
  )
}

const EDIT_FIELDS: [keyof LicenseCompany, string][] = [
  ["legal_name", "Razão social"], ["trade_name", "Nome fantasia"], ["document", "CNPJ / CPF"], ["state_registration", "Inscrição estadual"],
  ["email", "E-mail financeiro"], ["phone", "Telefone"], ["whatsapp", "WhatsApp"], ["responsible_name", "Responsável"],
  ["responsible_email", "E-mail do responsável"], ["responsible_phone", "Telefone do responsável"], ["postal_code", "CEP"], ["street", "Logradouro"],
  ["street_number", "Número"], ["address_complement", "Complemento"], ["neighborhood", "Bairro"], ["city", "Cidade"], ["state", "UF"],
]

function CompanyEditDialog({ company, onClose }: { company: LicenseCompany | null; onClose: (saved?: LicenseCompany) => void }) {
  const save = useSaveLicenseCompany()
  const [draft, setDraft] = useState<LicenseCompany | null>(company)
  useEffect(() => setDraft(company), [company])
  if (!draft) return null
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar empresa</DialogTitle>
          <DialogDescription>Dados cadastrais da empresa dona da licença.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {EDIT_FIELDS.map(([name, label]) => (
            <div key={name} className="space-y-1.5">
              <Label className="text-[12px]">{label}</Label>
              <Input className="h-9" value={String(draft[name] || "")} onChange={(e) => setDraft({ ...draft, [name]: e.target.value })} />
            </div>
          ))}
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-[12px]">Observações</Label>
            <Textarea value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose()}>Cancelar</Button>
          <Button
            disabled={!draft.legal_name.trim() || save.isPending}
            onClick={async () => {
              await save.mutateAsync(draft)
              onClose(draft)
            }}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type Filter = "all" | "valid" | "trial" | "attention" | "archived"

const LicenseManagement = () => {
  const { data: licenses = [], isLoading, error } = useLicenses()
  const toggle = useToggleLicense()
  const archive = useDeleteLicense()
  const renew = useRenewLicense()
  const resetMachines = useResetLicenseMachines()
  const [searchParams, setSearchParams] = useSearchParams()
  const [openCreate, setOpenCreate] = useState(false)
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<Filter>("all")
  const [viewing, setViewing] = useState<License | null>(null)
  const [editingCompany, setEditingCompany] = useState<LicenseCompany | null>(null)
  const [renewing, setRenewing] = useState<License | null>(null)
  const [renewDays, setRenewDays] = useState("30")
  const [confirm, setConfirm] = useState<{ kind: "reset" | "archive"; license: License } | null>(null)
  const [success, setSuccess] = useState<{ key: string; company: string } | null>(null)

  useEffect(() => {
    if (searchParams.get("nova") !== "1") return
    setOpenCreate(true)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  const activeMachines = (l: License) => l.activations?.filter((a) => !a.revoked_at).length || 0

  const counts = useMemo(() => ({
    all: licenses.filter((l) => l.status !== "archived").length,
    valid: licenses.filter((l) => l.status === "active").length,
    trial: licenses.filter((l) => l.status === "trial").length,
    attention: licenses.filter((l) => ATTENTION.includes(l.status)).length,
    archived: licenses.filter((l) => l.status === "archived").length,
  }), [licenses])
  const devices = licenses.reduce((t, l) => t + activeMachines(l), 0)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return licenses.filter((l) => {
      const matchesFilter =
        filter === "all" ? l.status !== "archived"
        : filter === "valid" ? l.status === "active"
        : filter === "attention" ? ATTENTION.includes(l.status)
        : l.status === filter
      const matchesQuery = !q || [l.company?.legal_name, l.company?.trade_name, l.company?.document, l.key_preview, l.license_key, l.plan]
        .some((v) => String(v || "").toLowerCase().includes(q))
      return matchesFilter && matchesQuery
    })
  }, [licenses, query, filter])

  const pager = usePagination(filtered, 12)
  useEffect(() => pager.reset(), [query, filter]) // eslint-disable-line react-hooks/exhaustive-deps

  const companyName = (l: License) => l.company?.trade_name || l.company?.legal_name || l.client_name

  const actionsMenu = (l: License) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={() => setViewing(l)}><KeyRound className="mr-2 h-4 w-4" />Ver detalhes</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => { setRenewDays("30"); setRenewing(l) }}><RefreshCw className="mr-2 h-4 w-4" />Renovar prazo</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setConfirm({ kind: "reset", license: l })}><Laptop className="mr-2 h-4 w-4" />Liberar computadores</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => toggle.mutate({ id: l.id, is_active: !l.is_active })}>
          {l.is_active ? <ShieldOff className="mr-2 h-4 w-4" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
          {l.is_active ? "Suspender" : "Reativar"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setConfirm({ kind: "archive", license: l })} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><Archive className="mr-2 h-4 w-4" />Arquivar</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <>
      <StatGrid>
        <StatCard label="Licenças ativas" value={counts.valid} icon={ShieldCheck} tone="success" hint={`${counts.all} emitidas`} />
        <StatCard label="Em avaliação" value={counts.trial} icon={KeyRound} tone="accent" hint="Período de teste" />
        <StatCard label="Precisam de atenção" value={counts.attention} icon={AlertTriangle} tone={counts.attention ? "warning" : "neutral"} hint={counts.attention ? "Inadimplentes, suspensas ou vencidas" : "Nenhuma pendência"} />
        <StatCard label="Computadores ativos" value={devices} icon={Laptop} hint="Máquinas com a chave ativada" />
      </StatGrid>

      <Panel flush>
        <Toolbar>
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: "Todas", count: counts.all },
              { value: "valid", label: "Ativas", count: counts.valid },
              { value: "trial", label: "Avaliação", count: counts.trial },
              { value: "attention", label: "Atenção", count: counts.attention },
              { value: "archived", label: "Arquivadas", count: counts.archived },
            ]}
          />
          <SearchInput value={query} onChange={setQuery} placeholder="Empresa, CNPJ, plano ou chave" className="lg:ml-auto" />
        </Toolbar>

        {isLoading ? (
          <SkeletonRows />
        ) : error ? (
          <div className="p-6 text-center text-[13px] text-[hsl(358_90%_72%)]">{error instanceof Error ? error.message : "Falha ao carregar licenças."}</div>
        ) : licenses.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title="Nenhuma chave emitida ainda"
            description="Cadastre a empresa, gere a chave e acompanhe as máquinas que ativarem."
            action={<Button size="sm" onClick={() => setOpenCreate(true)}>Emitir primeira licença</Button>}
          />
        ) : filtered.length === 0 ? (
          <EmptyState icon={KeyRound} title="Nenhuma licença neste filtro" description="Ajuste a busca ou o status." />
        ) : (
          <>
            <DataTable minWidth={860}>
              <thead>
                <tr className="border-b border-border">
                  <th className={th}>Empresa</th>
                  <th className={th}>Plano</th>
                  <th className={th}>Status</th>
                  <th className={th}>Validade</th>
                  <th className={cn(th, "text-right")}>Máquinas</th>
                  <th className={th}>Último contato</th>
                  <th className={cn(th, "w-px")}><span className="sr-only">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {pager.slice.map((l) => {
                  const st = STATUS[l.status] ?? { label: l.status, tone: "neutral" as Tone }
                  const expiry = l.expires_at || l.expiration_date
                  const used = activeMachines(l)
                  const max = l.max_devices || 1
                  return (
                    <tr key={l.id} className={rowClass} onClick={() => setViewing(l)}>
                      <td className={td}>
                        <div className="flex items-center gap-3">
                          <IconTile icon={Building2} />
                          <div className="min-w-0">
                            <div className="max-w-[240px] truncate text-[13px] font-medium text-foreground">{companyName(l)}</div>
                            <div className="max-w-[240px] truncate text-[12px] text-muted-foreground">{l.company?.document || "Documento não informado"}</div>
                          </div>
                        </div>
                      </td>
                      <td className={td}>
                        <div className="text-[13px] text-foreground">{l.plan}</div>
                        <code className="font-mono text-[11px] text-muted-foreground">{l.key_preview || `${l.license_key.slice(0, 10)}…`}</code>
                      </td>
                      <td className={td}><Tag tone={st.tone} dot>{st.label}</Tag></td>
                      <td className={td}>
                        <div className="text-[13px] tabular-nums text-foreground">{expiry ? fmtDateBR(expiry) : "Sem vencimento"}</div>
                        {expiry && <div className="text-[11px] text-muted-foreground">{relativeDays(expiry)}</div>}
                      </td>
                      <td className={cn(td, "text-right")}>
                        <span className={cn("text-[13px] font-medium tabular-nums", used >= max ? "text-[hsl(38_92%_68%)]" : "text-foreground")}>{used}/{max}</span>
                      </td>
                      <td className={cn(td, "text-[13px] text-muted-foreground")}>
                        {l.last_validated_at ? relativeDays(l.last_validated_at) : "Nunca"}
                        {l.app_version && <div className="text-[11px]">v{l.app_version}</div>}
                      </td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>{actionsMenu(l)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </DataTable>
            <Pagination {...pager} noun="licenças" />
          </>
        )}
      </Panel>

      <DetailSheet
        open={!!viewing}
        onOpenChange={(o) => !o && setViewing(null)}
        badge={viewing && <Tag tone={(STATUS[viewing.status] ?? STATUS.archived).tone} dot className="self-start">{(STATUS[viewing.status] ?? STATUS.archived).label}</Tag>}
        title={viewing && companyName(viewing)}
        subtitle={viewing && `${viewing.plan} · ${viewing.license_type === "perpetual" ? "Perpétua" : viewing.license_type === "trial" ? "Avaliação" : "Assinatura"}`}
        actions={viewing && (
          <>
            <CopyKey value={viewing.license_key} />
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => { setRenewDays("30"); setRenewing(viewing) }}><RefreshCw className="h-3.5 w-3.5" />Renovar</Button>
            {actionsMenu(viewing)}
          </>
        )}
      >
        {viewing && (
          <>
            <DetailSection title="Licença">
              <DetailList items={[
                ["Chave", <code key="k" className="break-all font-mono text-[11px]">{viewing.license_key}</code>],
                ["Validade", (viewing.expires_at || viewing.expiration_date) ? `${fmtDateBR(viewing.expires_at || viewing.expiration_date)} (${relativeDays(viewing.expires_at || viewing.expiration_date)})` : "Sem vencimento"],
                ["Máquinas", `${activeMachines(viewing)} de ${viewing.max_devices || 1}`],
                ["Offline máximo", `${viewing.max_offline_days} dias`],
                ["Carência", `${viewing.grace_days} dias`],
                ["Validações", viewing.validations_count],
                ["Emitida em", fmtDateBR(viewing.created_at, true)],
                ["Observações", viewing.notes],
              ]} />
            </DetailSection>

            <DetailSection title={`Computadores (${viewing.activations?.length ?? 0})`}>
              {!viewing.activations?.length ? (
                <p className="rounded-lg border border-border p-3 text-[13px] text-muted-foreground">Nenhuma máquina ativou esta chave ainda.</p>
              ) : (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {viewing.activations.map((a) => (
                    <li key={a.id} className={cn("flex items-center gap-3 px-3 py-2.5", a.revoked_at && "opacity-50")}>
                      <Laptop className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] text-foreground">{a.machine_label || a.machine_id}</div>
                        <div className="text-[11px] text-muted-foreground">Ativada {fmtDateBR(a.first_activated_at)} · visto {relativeDays(a.last_seen_at)}{a.app_version && ` · v${a.app_version}`}</div>
                      </div>
                      {a.revoked_at ? <Tag>Liberada</Tag> : <Tag tone="success" dot>Ativa</Tag>}
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>

            {viewing.company && (
              <DetailSection
                title="Empresa"
                action={<Button size="sm" variant="ghost" className="h-7 gap-1.5 text-[12px] text-muted-foreground" onClick={() => setEditingCompany(viewing.company)}><Pencil className="h-3 w-3" />Editar</Button>}
              >
                <DetailList items={[
                  ["Razão social", viewing.company.legal_name],
                  ["Nome fantasia", viewing.company.trade_name],
                  ["CNPJ / CPF", viewing.company.document],
                  ["E-mail financeiro", viewing.company.email],
                  ["Telefone", viewing.company.phone || viewing.company.whatsapp],
                  ["Responsável", [viewing.company.responsible_name, viewing.company.responsible_phone].filter(Boolean).join(" · ")],
                  ["Endereço", [viewing.company.street, viewing.company.street_number, viewing.company.neighborhood, viewing.company.city, viewing.company.state].filter(Boolean).join(", ")],
                ]} />
              </DetailSection>
            )}
          </>
        )}
      </DetailSheet>

      <Dialog open={!!renewing} onOpenChange={(o) => !o && setRenewing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Renovar licença</DialogTitle>
            <DialogDescription>{renewing && companyName(renewing)} — quantos dias acrescentar ao prazo atual?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            {["30", "90", "180", "365"].map((d) => (
              <Button key={d} type="button" size="sm" variant={renewDays === d ? "default" : "outline"} className="h-8 flex-1" onClick={() => setRenewDays(d)}>{d}d</Button>
            ))}
          </div>
          <Input type="number" min="1" value={renewDays} onChange={(e) => setRenewDays(e.target.value)} className="h-9" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenewing(null)}>Cancelar</Button>
            <Button
              disabled={!Number(renewDays)}
              onClick={() => {
                if (renewing) renew.mutate({ action: "renew", licenseId: renewing.id, days: Number(renewDays) })
                setRenewing(null)
              }}
            >
              Renovar {renewDays} dias
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === "reset" ? "Liberar computadores?" : "Arquivar licença?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "reset"
                ? "Todas as máquinas desta chave serão desvinculadas e poderão ser ativadas de novo (troca de equipamento)."
                : "A chave deixa de funcionar e sai da lista principal. O histórico continua guardado em Arquivadas."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirm) return
                if (confirm.kind === "reset") resetMachines.mutate({ action: "reset_machines", licenseId: confirm.license.id, reason: "Troca de equipamento autorizada" })
                else {
                  archive.mutate(confirm.license.id)
                  setViewing(null)
                }
              }}
            >
              {confirm?.kind === "reset" ? "Liberar" : "Arquivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LicenseModal
        isOpen={openCreate}
        onClose={(key?: string, company?: string) => {
          setOpenCreate(false)
          if (key && company) setSuccess({ key, company })
        }}
      />
      <CompanyEditDialog
        company={editingCompany}
        onClose={(saved) => {
          if (saved && viewing) setViewing({ ...viewing, company: saved })
          setEditingCompany(null)
        }}
      />
      {success && (
        <Dialog open onOpenChange={(o) => !o && setSuccess(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Licença emitida</DialogTitle>
              <DialogDescription>A chave está vinculada a <strong className="text-foreground">{success.company}</strong> e fica disponível nos detalhes.</DialogDescription>
            </DialogHeader>
            <code className="block break-all rounded-lg border border-dashed border-border bg-[hsl(228_8%_9%)] p-4 font-mono text-[12px]">{success.key}</code>
            <DialogFooter><CopyKey value={success.key} /></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export { LicenseManagement }
